import { useEffect, useState } from "react";
import { Loader2, Send, Eye, MousePointerClick, MessageCircle, AlertTriangle } from "lucide-react";
import { http } from "@/lib/api";

const STATUS_TONE = {
  sent:   "bg-[#E6F4EA] text-[#1B5E20]",
  draft:  "bg-ink-100 text-ink-700",
  failed: "bg-[#FCE8E6] text-[#C5221F]",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

/**
 * OutreachActivity — recent sent emails timeline.
 * Pulls /outreach/sent (last 200) and renders status, opens, clicks, replies per row.
 */
export default function OutreachActivity() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/outreach/sent");
      setItems(data.emails || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="grid place-items-center py-16 text-ink-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white border border-dashed border-ink-200 rounded-sm p-12 text-center" data-testid="activity-empty">
        <Send className="w-7 h-7 mx-auto text-ink-300 mb-3" />
        <h3 className="font-display text-lg font-bold text-ink-900 mb-1">No activity yet</h3>
        <p className="text-ink-500 text-sm max-w-md mx-auto">
          Recent emails sent from your account — manual drafts or sequence steps —
          will show up here with open, click, and reply tracking.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-ink-100 rounded-sm overflow-hidden" data-testid="outreach-activity">
      <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-bold text-ink-900">Recent activity</h3>
          <p className="text-[11px] text-ink-400 mt-0.5">
            Last {items.length} sent email{items.length === 1 ? "" : "s"} — newest first.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="activity-refresh"
          className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500 hover:text-ink-900 transition-colors"
        >
          Refresh
        </button>
      </div>
      <div className="divide-y divide-ink-100">
        {items.map((m) => {
          const tone = STATUS_TONE[m.status] || "bg-ink-100 text-ink-700";
          return (
            <div
              key={m.id}
              className="px-5 py-3 grid grid-cols-12 gap-3 items-center hover:bg-ink-50/40 transition-colors"
              data-testid={`activity-row-${m.id}`}
            >
              <div className="col-span-5 min-w-0">
                <div className="font-semibold text-ink-900 text-sm truncate">
                  {m.subject || "(no subject)"}
                </div>
                <div className="text-[11px] font-mono text-ink-500 truncate mt-0.5">
                  → {m.to || "—"}
                  {m.kind === "sequence" && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 bg-[#FFF4ED] text-brand text-[9px] uppercase tracking-widestx font-semibold rounded-sm">
                      seq · step {(m.sequence_step_index ?? 0) + 1}
                    </span>
                  )}
                </div>
              </div>
              <div className="col-span-2">
                <span
                  className={`inline-block text-[10px] uppercase tracking-widestx font-semibold px-2 py-0.5 rounded-sm ${tone}`}
                >
                  {m.status || "draft"}
                </span>
                {m.status === "failed" && m.send_error && (
                  <div className="mt-1 text-[10px] text-[#C5221F] inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    <span className="truncate max-w-[200px]" title={m.send_error}>
                      {m.send_error}
                    </span>
                  </div>
                )}
              </div>
              <div className="col-span-3 flex items-center gap-3 text-[11px] font-mono text-ink-500">
                <span className="inline-flex items-center gap-1" title="opens">
                  <Eye className="w-3 h-3" /> {m.open_count || 0}
                </span>
                <span className="inline-flex items-center gap-1" title="clicks">
                  <MousePointerClick className="w-3 h-3" /> {m.click_count || 0}
                </span>
                <span className="inline-flex items-center gap-1" title="replies">
                  <MessageCircle className="w-3 h-3" /> {m.replied_at ? 1 : 0}
                </span>
              </div>
              <div className="col-span-2 text-right text-[11px] font-mono text-ink-400">
                {timeAgo(m.sent_at || m.created_at)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
